import Fuse from 'fuse.js';
import { useMemo } from 'react';

import { ArtifactType } from '@/services/types/artifact-digest-types';
import { ArtifactUnpacked } from '@/services/types/artifact-types';

interface UseArtifactFilteringProps {
  artifacts: ArtifactUnpacked[] | undefined;
  searchQuery: string;
  typeFilter: ArtifactType | 'unprocessed' | 'all';
  sortOrder: 'asc' | 'desc';
}

export const useArtifactFiltering = ({
  artifacts,
  searchQuery,
  typeFilter,
  sortOrder,
}: UseArtifactFilteringProps) => {
  const processedArtifacts = useMemo(() => {
    if (!artifacts) return [];

    // Filter out audio conversations (we show transcripts instead)
    let filtered = artifacts.filter(
      (artifact) => artifact.artifact_type !== ArtifactType.AUDIO_CONVERSATION,
    );

    // Filter by type or processing status if specified
    if (typeFilter !== 'all') {
      if (typeFilter === 'unprocessed') {
        filtered = filtered.filter((artifact) => !artifact.artifact_digest);
      } else {
        filtered = filtered.filter(
          (artifact) => artifact.artifact_type === typeFilter,
        );
      }
    }

    // Search filter using Fuse.js for fuzzy matching
    if (searchQuery.trim()) {
      const fuse = new Fuse(filtered, {
        keys: [
          'artifact_digest.title',
          'artifact_digest.one_line_summary',
          'artifact_digest.long_form_description',
          'blob.name',
          'blob.original_filename',
        ],
        threshold: 0.3,
        ignoreLocation: true,
      });

      filtered = fuse.search(searchQuery.trim()).map((result) => result.item);
    }

    // Sort by created date
    return filtered.sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });
  }, [artifacts, sortOrder, searchQuery, typeFilter]);

  // Quick stats
  const stats = useMemo(() => {
    if (!artifacts) return null;

    const filtered = artifacts.filter(
      (a) => a.artifact_type !== ArtifactType.AUDIO_CONVERSATION,
    );
    const typeStats = filtered.reduce(
      (acc, artifact) => {
        acc[artifact.artifact_type] = (acc[artifact.artifact_type] || 0) + 1;
        return acc;
      },
      {} as Record<ArtifactType, number>,
    );

    return {
      total: filtered.length,
      withDigest: filtered.filter((a) => a.artifact_digest).length,
      typeStats,
    };
  }, [artifacts]);

  // Available types for filter dropdown
  const availableTypes = useMemo(() => {
    if (!artifacts) return [];

    const types = new Set(
      artifacts
        .filter((a) => a.artifact_type !== ArtifactType.AUDIO_CONVERSATION)
        .map((a) => a.artifact_type),
    );

    return Array.from(types);
  }, [artifacts]);

  return {
    processedArtifacts,
    stats,
    availableTypes,
  };
};
